import React, { useState } from "react";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import {
  formatPrice,
  formatSaleDateTime,
  getSaleThumbnailSrc,
} from "../utils/saleUtils";

const recentSalesTableStyle = {
  width: "100%",
  minWidth: "720px",
  borderCollapse: "separate",
  borderSpacing: "0 10px",
  tableLayout: "fixed",
};

const recentSalesHeaderCellStyle = {
  border: "none",
  padding: "8px 16px",
  fontSize: "13px",
  fontWeight: 600,
  color: "#6c757d",
  letterSpacing: "0.02em",
  verticalAlign: "bottom",
};

const recentSalesBodyCellStyle = {
  border: "none",
  padding: "14px 16px",
  verticalAlign: "middle",
  backgroundColor: "#fff",
};

const recentSalesRowShadowStyle = {
  filter: "drop-shadow(0 2px 8px rgba(99, 99, 99, 0.2))",
};

function SaleThumbnail({ productDetails, title }) {
  const [hasError, setHasError] = useState(false);
  const src = getSaleThumbnailSrc(productDetails);
  const showPlaceholder = !src || hasError;

  if (showPlaceholder) {
    return (
      <div
        className="rounded-3 d-flex align-items-center justify-content-center"
        style={{
          width: "80px",
          height: "80px",
          backgroundColor: "#E9ECEF",
          color: "#ADB5BD",
        }}
        aria-hidden="true"
      >
        <ImageNotSupportedOutlinedIcon fontSize="small" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={title ? `${title} thumbnail` : "Product thumbnail"}
      className="rounded-3"
      style={{
        width: "80px",
        height: "80px",
        objectFit: "cover",
        objectPosition: "center",
        display: "block",
      }}
      onError={() => setHasError(true)}
    />
  );
}

/**
 * Aligned recent sales/transactions list (seller + admin dashboards).
 * Uses the same table structure as Purchases for header/row column alignment.
 */
export function RecentSalesList({ items, emptyMessage = "No sales yet." }) {
  if (!items?.length) {
    return <p className="text-muted mb-0">{emptyMessage}</p>;
  }

  return (
    <div className="recent-sales-list sales-items-wrapper" style={{ overflowX: "auto" }}>
      <table style={recentSalesTableStyle}>
        <colgroup>
          <col style={{ width: "96px" }} />
          <col />
          <col style={{ width: "12%" }} />
          <col style={{ width: "24%" }} />
          <col style={{ width: "16%" }} />
        </colgroup>
        <thead>
          <tr>
            <th scope="col" aria-hidden="true" style={recentSalesHeaderCellStyle} />
            <th
              scope="col"
              style={{ ...recentSalesHeaderCellStyle, textAlign: "left" }}
            >
              Product
            </th>
            <th
              scope="col"
              style={{ ...recentSalesHeaderCellStyle, textAlign: "left" }}
            >
              Price
            </th>
            <th
              scope="col"
              style={{ ...recentSalesHeaderCellStyle, textAlign: "left" }}
            >
              Date
            </th>
            <th
              scope="col"
              style={{ ...recentSalesHeaderCellStyle, textAlign: "left" }}
            >
              Buyer
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => {
            const title = item?.product_details?.title;
            const description = item?.product_details?.short_description;

            return (
              <tr key={item?.id ?? index} style={recentSalesRowShadowStyle}>
                <td
                  style={{
                    ...recentSalesBodyCellStyle,
                    borderRadius: "16px 0 0 16px",
                    paddingLeft: "20px",
                    width: "96px",
                  }}
                >
                  <SaleThumbnail
                    productDetails={item?.product_details}
                    title={title}
                  />
                </td>
                <td style={recentSalesBodyCellStyle}>
                  <p
                    className="mb-0 fw-semibold text-truncate"
                    style={{ fontSize: "14px" }}
                    title={title}
                  >
                    {title || "—"}
                  </p>
                  <p
                    className="mb-0  text-truncate"
                    style={{ fontSize: "13px" }}
                    title={description}
                  >
                    {description || "—"}
                  </p>
                </td>
                <td style={recentSalesBodyCellStyle}>
                  <p className="mb-0 d-flex align-items-center flex-wrap gap-2">
                    <span className="fw-semibold" style={{ fontSize: "14px" }}>
                      {formatPrice(item?.price)}
                    </span>
                    {item?.is_refunded ? (
                      <span
                        className="badge rounded-pill"
                        style={{
                          backgroundColor: "#dc3545",
                          fontSize: "10px",
                        }}
                      >
                        Refunded
                      </span>
                    ) : null}
                  </p>
                </td>
                <td style={recentSalesBodyCellStyle}>
                  <p
                    className="mb-0"
                    style={{ fontSize: "13px" }}
                    title={item?.created_at}
                  >
                    {formatSaleDateTime(item?.created_at)}
                  </p>
                </td>
                <td
                  style={{
                    ...recentSalesBodyCellStyle,
                    borderRadius: "0 16px 16px 0",
                    paddingRight: "20px",
                  }}
                >
                  <p
                    className="mb-0 text-truncate"
                    style={{ fontSize: "13px" }}
                    title={item?.buyer_name}
                  >
                    {item?.buyer_name || "—"}
                  </p>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default RecentSalesList;
